import { useMemo, useState } from 'react';
import { TextInput, ScrollArea, Table, Group, Text, Skeleton, Stack, Box } from '@mantine/core';
import { IconSearch } from '@tabler/icons-react';
import { useVisitors } from '../api/hooks/useVisitors.ts';

/**
 * Left-column visitor picker. Search input on top, three-column mini-table
 * below (First Name / Last Name / Centre). Clicking a row notifies the
 * parent which loads the visitor into the centre column.
 */
export function VisitorListPanel({
  roleId,
  selectedId,
  onSelect,
}: {
  roleId: number | null;
  selectedId: number | null;
  onSelect(id: number): void;
}) {
  const list = useVisitors({ role: roleId ?? -1, page: 0, size: 1000 });
  const [filter, setFilter] = useState('');

  const rows = useMemo(() => {
    const f = filter.trim().toLowerCase();
    const all = list.data?.rows ?? [];
    if (!f) return all;
    return all.filter(v =>
      [v.firstName, v.lastName, v.email, v.phones, v.centre]
        .filter(Boolean)
        .some(s => String(s).toLowerCase().includes(f)),
    );
  }, [list.data?.rows, filter]);

  return (
    <Stack gap="xs" h="100%" style={{ minHeight: 0 }}>
      <TextInput
        leftSection={<IconSearch size={16} />}
        placeholder="Search"
        value={filter}
        onChange={e => setFilter(e.currentTarget.value)}
        size="sm"
      />
      {roleId == null || list.isPending ? (
        <Stack gap={4}>
          {[1, 2, 3, 4, 5].map(i => <Skeleton key={i} h={24} />)}
        </Stack>
      ) : list.isError ? (
        <Text c="red" size="sm">Failed to load visitors.</Text>
      ) : rows.length === 0 ? (
        <Text c="dimmed" size="sm" ta="center" pt="md">
          {filter ? 'No matches.' : 'No visitors in this role.'}
        </Text>
      ) : (
        <Box style={{ flex: 1, minHeight: 0 }}>
          <ScrollArea h="100%" type="auto">
            <Table highlightOnHover stickyHeader withRowBorders={false} verticalSpacing="xs" fz="sm">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>First Name</Table.Th>
                  <Table.Th>Last Name</Table.Th>
                  <Table.Th>Centre</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {rows.map(v => {
                  const selected = v.id === selectedId;
                  return (
                    <Table.Tr
                      key={v.id}
                      onClick={() => v.id != null && onSelect(v.id)}
                      style={{
                        cursor: 'pointer',
                        background: selected ? 'var(--mantine-color-blue-light)' : undefined,
                      }}
                    >
                      <Table.Td>{v.firstName ?? ''}</Table.Td>
                      <Table.Td>{v.lastName ?? ''}</Table.Td>
                      <Table.Td>{v.centre ?? ''}</Table.Td>
                    </Table.Tr>
                  );
                })}
              </Table.Tbody>
            </Table>
          </ScrollArea>
        </Box>
      )}
    </Stack>
  );
}
